/**
 * The choices that change how Grow looks and reads: language, units, the look,
 * the text size, Today's picture and how a card shows its photos.
 *
 * Each is a short choice, so each is a row of buttons rather than a dropdown,
 * and each is kept the moment it is made with "Saved" beside it. They used to
 * be four dropdowns and a switch, a toast for some and silence for one.
 *
 * Used by: UserProfile (?s=prefs). Everything but the card photos is saved
 * to the account (preferences); the card photos choice is kept on the device
 * only (shared/lib/localPref), and Today's picture is its own component.
 *
 * A save that fails on our side (a 5xx or no answer) says the standard
 * sentence, reported to Sentry, with the support address as a link (D31);
 * any other failure keeps profile.preferences.toast.saveFailed.
 *
 * @module ProfilePreferences
 */

import React from 'react';
import { CARD_PHOTOS_KEY, CARD_PHOTOS_VALUES, useLocalPref, type CardPhotos } from '@/shared/lib/localPref';
import { useUserConfig, type Preferences } from '@/shared/context/userConfigStore';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { languages, setLanguage } from '@/shared/i18n/i18nSetup';
import { Language } from '@/shared/types/i18n';
import { debugLog } from '@/config/config';
import { ProfileScreen } from './ProfileScreen';
import { Segmented, SettingList, SettingRow } from './profileKit';
import { useSavedTicks } from './useSavedTicks';
import { TodayPictureSetting } from '@/features/today/components/TodayPictureSetting';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';

/** The looks a gardener can pick. */
type Theme = 'grow' | 'light' | 'dark' | 'fieldnotes';
/** The text sizes. */
type FontSize = 'small' | 'medium' | 'large';

/** The preferences screen: each choice kept as it is made. */
export const PreferencesScreen: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [cardPhotos, setCardPhotos] = useLocalPref(CARD_PHOTOS_KEY, 'peek', CARD_PHOTOS_VALUES);
  const { reactTranslate: t } = useTranslation();
  const { userConfig, savePreferences } = useUserConfig();
  const { preferences } = userConfig;
  const { saved, flash } = useSavedTicks();
  const [error, setError] = React.useState<string | null>(null);

  /**
   * Keeps one choice, then says so beside it.
   *
   * The language is switched only once it has been kept: switching first
   * and failing after left the app in a language the account did not have.
   *
   * @param key - The row whose "Saved" tick shows.
   * @param patch - The preferences that change.
   * @param after - What to do once kept (switching the language).
   */
  const keep = async (key: string, patch: Partial<Preferences>, after?: () => void): Promise<void> => {
    setError(null);
    try {
      await savePreferences({ ...preferences, ...patch } as Preferences);
      after?.();
      flash(key);
    } catch (err) {
      debugLog('UserProfile', 'Could not save a preference:', err);
      setError(errorMessage(err, t('profile.preferences.toast.saveFailed')));
    }
  };

  return (
    <ProfileScreen open={open} title={t('profile.index.prefs')} onClose={onClose}>
      <SettingList>
        <SettingRow title={t('sitewide.language')} saved={saved('language')}>
          <Segmented<Language>
            label={t('sitewide.language')}
            value={preferences.language}
            options={Object.entries(languages).map(([code, name]) => ({ value: code as Language, label: name as string }))}
            onChange={(lang) => void keep('language', { language: lang }, () => setLanguage(lang))}
          />
        </SettingRow>

        <SettingRow title={t('profile.preferences.temperature.title')} saved={saved('units')}>
          <Segmented<'F' | 'C'>
            label={t('profile.preferences.temperature.title')}
            value={preferences.temperatureUnit === 'C' ? 'C' : 'F'}
            options={[{ value: 'F', label: '°F' }, { value: 'C', label: '°C' }]}
            onChange={(unit) => void keep('units', { temperatureUnit: unit })}
          />
        </SettingRow>

        <SettingRow title={t('profile.preferences.theme.title')} hint={t(`profile.preferences.theme.description.${preferences.theme}`)} saved={saved('theme')} stacked>
          <Segmented<Theme>
            label={t('profile.preferences.theme.title')}
            value={preferences.theme as Theme}
            options={(['grow', 'light', 'dark', 'fieldnotes'] as Theme[]).map((value) => ({ value, label: t(`profile.preferences.theme.option.${value}`) }))}
            onChange={(theme) => void keep('theme', { theme })}
          />
        </SettingRow>

        <SettingRow title={t('settings.fontSize.title')} saved={saved('font')} stacked>
          <Segmented<FontSize>
            label={t('settings.fontSize.title')}
            value={preferences.fontSize as FontSize}
            options={(['small', 'medium', 'large'] as FontSize[]).map((value) => ({ value, label: t(`settings.fontSize.${value}`) }))}
            onChange={(fontSize) => void keep('font', { fontSize })}
          />
        </SettingRow>

        {/* The season's painting or a photo of the garden: chosen here, not
            with a pencil on the picture. */}
        <SettingRow title={t('profile.preferences.todayPicture.title')} hint={t('profile.preferences.todayPicture.hint')} stacked>
          <TodayPictureSetting />
        </SettingRow>

        {/* Kept on this phone rather than the account, like the way the
            garden is viewed, and now it says so. */}
        <SettingRow
          title={t('profile.preferences.cardPhotos.title')}
          hint={`${t(`profile.preferences.cardPhotos.${cardPhotos}`)} ${t('profile.preferences.deviceOnly')}`}
          saved={saved('photos')}
          stacked
        >
          <Segmented<CardPhotos>
            label={t('profile.preferences.cardPhotos.title')}
            value={cardPhotos}
            options={CARD_PHOTOS_VALUES.map((value) => ({ value, label: t(`profile.preferences.cardPhotos.option.${value}`) }))}
            onChange={(value) => { setCardPhotos(value); flash('photos'); }}
          />
        </SettingRow>
      </SettingList>
      {error && <p className="text-sm text-destructive"><FailureText text={error} /></p>}
    </ProfileScreen>
  );
};
